import { useCallback, useEffect } from 'react';
import {
  Button,
  Center,
  ClickAwayListener,
  Column,
  CustomSpinner,
  Pressable,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ProfessionTypeEnum } from '~/models';
import {
  firePopUpViewed,
  fireInviteSent,
  type TInvitePopupSource,
} from '~/posthogs/events';
import CloseIcon from '../../images/close-icon.svg';
import {
  Container,
  FavoriteTabPanel,
  Overlay,
  RecentProfessionalTabPanel,
  RecommendedProsTabPanel,
} from './components';
import { useInviteProfessional } from './hooks';

interface IProps {
  isOpen: boolean;
  isRefetchJob: boolean;
  confirmButtonText?: string;
  jobId?: string;
  jobIds?: string[];
  defaultProfessionals?: string[];
  profession?: string;
  officeId?: string;
  onClose: () => void;
  onConfirm?: (ids: string[]) => void;
  onTrackEvent?: () => void;
  trackingSource?: TInvitePopupSource;
}

export const InviteProfessionalPopup = ({
  isOpen,
  isRefetchJob,
  confirmButtonText = 'Send Invites',
  jobId,
  jobIds,
  defaultProfessionals,
  profession,
  officeId,
  onClose,
  onConfirm,
  onTrackEvent,
  trackingSource,
}: IProps) => {
  const {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    isRecommendedProsEnabled,
  } = useInviteProfessional({
    isRefetchJob,
    jobId,
    jobIds,
    defaultProfessionals,
    officeId,
    isOpen,
    onClosePopup: onClose,
    filterProfession: profession as ProfessionTypeEnum,
  });

  useEffect(() => {
    if (isOpen && trackingSource) {
      firePopUpViewed({ source: trackingSource });
    }
  }, [isOpen, trackingSource]);

  const handleConfirmProfessionals = useCallback(() => {
    onTrackEvent?.();

    if (onConfirm) {
      onConfirm([...selectedProfessionals]);

      return;
    }

    // Fire invite_sent event when popup actually sends invites
    if (trackingSource) {
      const selectedIds = [...selectedProfessionals];
      const favoriteCount = selectedIds.filter((id) =>
        favoriteProfessionals?.some((p: any) => p.id === id),
      ).length;
      const workedWithCount = selectedIds.filter((id) =>
        recentProfessionals?.some((p: any) => p.id === id || p.professional?.id === id),
      ).length;
      const newToYouCount = Math.max(0, selectedIds.length - favoriteCount - workedWithCount);

      fireInviteSent({
        shiftId: jobId || jobIds?.[0],
        totalInvitesSent: selectedIds.length,
        favoriteInvitesSent: favoriteCount,
        workedWithInvitesSent: workedWithCount,
        newToYouInvitesSent: newToYouCount,
        source: trackingSource,
      });
    }

    onSentInvitations();
  }, [
    onConfirm,
    onTrackEvent,
    selectedProfessionals,
    onSentInvitations,
    trackingSource,
    favoriteProfessionals,
    recentProfessionals,
    jobId,
    jobIds,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isSuccess ? (
        <Overlay>
          <ClickAwayListener onClickAway={() => onClosePopup()}>
            <Container>
              <Pressable onPress={onClosePopup}>
                <img
                  src={CloseIcon}
                  style={{
                    cursor: 'pointer',
                    height: 24,
                    position: 'absolute',
                    right: 20,
                    top: 20,
                    width: 24,
                  }}
                  alt="close_icon"
                />
              </Pressable>

              <Column marginBottom={24} marginTop={54}>
                <Text bold fontSize={18} textAlign="center">
                  Invite Professionals
                </Text>
              </Column>

              {isLoading && (
                <Center flex={1}>
                  <CustomSpinner isLoading size={32} />
                </Center>
              )}

              {!isLoading && (
                <>
                  <Tabs
                    initialTab={activeTab}
                    onChangeTab={onTabChange}
                    style={{ flex: 1 }}
                    tabListStyle={{
                      marginBottom: 0,
                    }}
                    tabPanelStyle={{
                      display: 'flex',
                      flex: 1,
                    }}
                  >
                    <TabList>
                      <Tab
                        title="Favorites"
                        totalCount={favoriteProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                      />
                      <Tab
                        title="Worked With"
                        totalCount={recentProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                      />
                      <Tab
                        title="New to You"
                        totalCount={recommendedProfessionals?.length}
                        textStyle={{
                          fontSize: 20,
                        }}
                        isVisible={isRecommendedProsEnabled}
                      />
                    </TabList>
                    <TabPanel>
                      <FavoriteTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={favoriteProfessionals ?? []}
                      />
                      <RecentProfessionalTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={recentProfessionals ?? []}
                      />
                      <RecommendedProsTabPanel
                        isLoading={isLoading}
                        selectedProfessionals={selectedProfessionals}
                        onSelectProfessionals={onSelectProfessionals}
                        profession={profession}
                        data={recommendedProfessionals}
                      />
                    </TabPanel>
                  </Tabs>
                  <Button
                    text={confirmButtonText}
                    disabled={selectedProfessionals.size === 0}
                    isLoading={isInviting && !isLoading}
                    style={{ marginInline: 'auto', width: 152, marginBottom: 32, marginTop: 24 }}
                    onClick={handleConfirmProfessionals}
                  />
                </>
              )}
            </Container>
          </ClickAwayListener>
        </Overlay>
      ) : (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <AnimatedCheckCircle />
            </div>
          )}
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonAction={onClosePopup}
          rightButtonText="Close"
        />
      )}
    </>
  );
};
